import React, { useState, useEffect } from 'react';
import { Box, Link, CircularProgress, Typography } from '@mui/joy';
import { LeftSidebar } from './LeftSidebar';
import { TopBar } from './TopBar';
import { Footer } from './Footer';
import { TrialNotification } from './TrialNotification';
import { ImpersonationBar, IMPERSONATION_BAR_HEIGHT } from './ImpersonationBar';
import { ImpersonateUserModal } from '../admin/ImpersonateUserModal';
import { useAuth } from '../../contexts/AuthContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useWebPush } from '../../hooks/useWebPush';
import SupportFloatingButton from '../support/SupportFloatingButton';
import NpsFooterPrompt from '../nps/NpsFooterPrompt';
import { BillingGate } from '../BillingGate';

interface AppLayoutProps {
  children: React.ReactNode;
}

interface SubscriptionInfo {
  status: string;
  days_remaining?: number;
  tier_name?: string;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const { isImpersonating, pickerOpen, closePicker } = useImpersonation();
  const barOffset = isImpersonating ? IMPERSONATION_BAR_HEIGHT : 0;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [showTrialNotification, setShowTrialNotification] = useState(false);

  // Update page title based on current route
  usePageTitle();

  // Subscribe to web push notifications (auto-subscribes when authenticated)
  useWebPush();

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const sidebarWidth = sidebarCollapsed ? 64 : 240;

  // Load subscription info for trial notifications
  useEffect(() => {
    const loadSubscriptionInfo = async () => {
      if (!user) return;
      
      try {
        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) return;

        const response = await fetch('/api/subscription/status/', {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setSubscriptionInfo(data);
          
          if (data.status === 'trialing' && data.days_remaining !== undefined && data.days_remaining >= 0) {
            setShowTrialNotification(true);
          }
        }
      } catch (error) {
        console.error('Error loading subscription info:', error);
      }
    };

    loadSubscriptionInfo();
  }, [user]);

  const handleSubscribe = () => {
    window.location.href = '/settings/billing';
  };

  const handleDismissTrialNotification = () => {
    setShowTrialNotification(false);
  };

  if (loading) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          <CircularProgress aria-label="Loading your workspace" /><Typography sx={{ ml: 2 }}>Loading your workspace…</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <ImpersonationBar />
      <ImpersonateUserModal open={pickerOpen} onClose={closePicker} />
      <Box data-app-shell sx={{ display: 'flex', minHeight: '100vh' }}>
        <Link href="#main-content" onClick={() => document.getElementById('main-content')?.focus()} sx={{ position: 'fixed', top: -100, left: 16, zIndex: 1500, p: 1.5, borderRadius: 'sm', bgcolor: 'background.surface', '&:focus': { top: 12 } }}>Skip to main content</Link>
        {/* Left Sidebar */}
        <LeftSidebar
          isCollapsed={sidebarCollapsed}
          onToggle={handleSidebarToggle}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
          topOffset={barOffset}
        />

        {/* Main Content Area */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            marginLeft: { xs: 0, md: `${sidebarWidth}px` },
            transition: 'margin-left 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
          }}
        >
          {/* Top Bar */}
          <TopBar sidebarWidth={sidebarWidth} topOffset={barOffset} onMobileMenuClick={() => setMobileOpen(true)} />

          {/* Trial Notification */}
          {showTrialNotification && subscriptionInfo && (
            <TrialNotification
              daysRemaining={subscriptionInfo.days_remaining || 0}
              tierName={subscriptionInfo.tier_name || 'Trial'}
              onSubscribe={handleSubscribe}
              onDismiss={handleDismissTrialNotification}
            />
          )}

          {/* Main Content */}
          <Box
            component="main"
            id="main-content"
            tabIndex={-1}
            sx={{
              flex: 1,
              marginTop: `${84 + barOffset}px`, // top bar height (84px) + impersonation bar offset
              backgroundColor: 'transparent', // let the body gradient show; glass cards float over it

              overflowX: 'hidden',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            <BillingGate>{children}</BillingGate>
          </Box>

          {/* Footer */}
          <Footer organizationName={user?.organization?.name} />
        </Box>
      </Box>

      {/* Support floating button — visible on all authenticated pages */}
      <SupportFloatingButton />
      {/* Bottom-left, below the support button's z-index: a survey must never
          cover the way to get help. Renders nothing unless the API offers a
          prompt, which it only does for eligible customer admins. */}
      <NpsFooterPrompt sidebarWidth={sidebarWidth} />
    </>
  );
};